import { Alert, Box, Stack, Typography } from '@mui/material'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import AvatarBoCan from '../../AvatarBoCan'
import { createAvatarBoClient } from '../../shared/AvatarBoClient'
import { AVATAR_BO_PERMISSIONS } from '../../shared/permissions'
import type { AvatarBoClient } from '../../shared/types'
import SnapshotProfileScreen from './SnapshotProfileScreen'

export interface AvatarSnapshotProfilePanelProps {
  apiBaseUrl: string
  authToken?: string
  client?: AvatarBoClient
}

/**
 * Which sizes, densities and formats a rendered avatar is written in.
 *
 * The same shape as the tier-ladder panel, and gated on the same permission: this is catalogue
 * policy, not artwork, and one role edits both.
 */
export default function AvatarSnapshotProfilePanel({
  apiBaseUrl,
  authToken,
  client: injected,
}: AvatarSnapshotProfilePanelProps) {
  const { formatMessage } = useIntl()
  const client = useMemo(
    () =>
      injected ??
      (apiBaseUrl
        ? createAvatarBoClient({ apiBaseUrl, authToken: () => authToken })
        : undefined),
    [injected, apiBaseUrl, authToken],
  )

  if (!client) {
    return (
      <Alert severity="warning">
        {formatMessage(
          { id: 'avatarStudio.notConfigured' },
          { localUrl: 'http://localhost:5001' },
        )}
      </Alert>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Typography variant="h5" component="h1">
          {formatMessage({ id: 'avatarStudio.imageSizes.title' })}
        </Typography>
        <AvatarBoCan
          perform={AVATAR_BO_PERMISSIONS.catalogueWrite}
          // tslint:disable-next-line: jsx-no-lambda
          yes={() => <SnapshotProfileScreen client={client} />}
          // tslint:disable-next-line: jsx-no-lambda
          no={() => (
            <Alert severity="error">
              {formatMessage(
                { id: 'avatarStudio.noPermission' },
                { permission: AVATAR_BO_PERMISSIONS.catalogueWrite },
              )}
            </Alert>
          )}
        />
      </Stack>
    </Box>
  )
}
